import { useState } from 'react'
import { Drawer, Button, Typography, Flex, Alert, Tag } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSparkles, faStar } from '@fortawesome/pro-solid-svg-icons'
import { CloseOutlined, MenuFoldOutlined } from '@ant-design/icons'
import { useChat } from './useChat'
import { openFeedbackSurvey } from './aiAssistantModel'
import { createLookupRequestMessage } from './formRequestModel'
import ChatWindow from './ChatWindow'
import MessageInput from './MessageInput'
import ChatHistoryDropdown from './ChatHistoryDropdown'
import './AiAssistant.css'

type Props = {
  open: boolean
  onClose: () => void
}

const AiAssistantDrawer = ({ open, onClose }: Props) => {
  const {
    messages,
    isStreaming,
    error,
    sessionId,
    ratings,
    pendingRatings,
    sendMessage,
    sendDecision,
    sendGroupDecision,
    submitForm,
    submitQuestionSet,
    lookupOptions,
    cancelStream,
    resetSession,
    loadSession,
    rateMessage,
    submitMessageRationale,
  } = useChat(open)
  const [fullScreen, setFullScreen] = useState(true)

  return (
    <Drawer
      title={
        <Flex align="center" gap={8}>
          <Button
            type="text"
            size="small"
            icon={<MenuFoldOutlined />}
            onClick={() => setFullScreen(!fullScreen)}
          />
          <FontAwesomeIcon icon={faSparkles} style={{ color: '#f47458' }} />
          <Typography.Text strong>AI Assistant</Typography.Text>
          <Tag color="orange" style={{ marginLeft: 0 }}>Early Preview</Tag>
        </Flex>
      }
      extra={
        <Flex align="center" gap={8}>
          <ChatHistoryDropdown
            onNewChat={resetSession}
            onSelectChat={loadSession}
          />
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={onClose}
          />
        </Flex>
      }
      placement="left"
      open={open}
      onClose={onClose}
      width={fullScreen ? '100vw' : '40vw'}
      styles={{
        body: {
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
        wrapper: {
          minWidth: 320,
        },
      }}
      closable={false}
      mask={false}
    >
      <Flex vertical style={{ height: '100%' }}>
        <Alert
          message="You're previewing an early version of our AI Assistant. Share your feedback!"
          type="info"
          showIcon
          action={
            <Button
              size="small"
              type="primary"
              onClick={openFeedbackSurvey}
              style={{ backgroundColor: '#f47458', borderColor: '#f47458', borderRadius: 16 }}
            >
              <FontAwesomeIcon icon={faStar} style={{ marginRight: 6 }} />
              Give Feedback
            </Button>
          }
          style={{ margin: 8, borderRadius: 6 }}
        />
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            style={{ margin: 8, borderRadius: 6 }}
          />
        )}
        <ChatWindow
          messages={messages}
          isStreaming={isStreaming}
          fullScreen={fullScreen}
          onSendMessage={sendMessage}
          onDecide={sendDecision}
          onGroupDecide={sendGroupDecision}
          onSubmitForm={submitForm}
          onSubmitQuestionSet={submitQuestionSet}
          onLookupSearch={lookupOptions}
          onLookupCreateRequest={(entity, query) =>
            sendMessage(createLookupRequestMessage(entity, query), true)
          }
          disabled={!sessionId}
          ratings={ratings}
          pendingRatings={pendingRatings}
          onRateMessage={rateMessage}
          onSubmitMessageRationale={submitMessageRationale}
        />
        <div style={{
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'center',
          padding: `12px ${fullScreen ? 100 : 16}px`,
        }}>
          <div style={{ maxWidth: 1440, width: '100%' }}>
            <MessageInput
              onSend={sendMessage}
              onCancel={cancelStream}
              isStreaming={isStreaming}
              disabled={!sessionId}
            />
            <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', fontSize: 12, marginTop: 8 }}>
              AI can make mistakes. Please double-check responses.
            </Typography.Text>
          </div>
        </div>
      </Flex>
    </Drawer>
  )
}

export default AiAssistantDrawer
